import Box from '@material-ui/core/Box';
import MUIButton from '@material-ui/core/Button';
import Popover from '@material-ui/core/Popover';
import { makeStyles } from '@material-ui/core/styles';
import React from 'react';
import {
	CRUD_UPDATE_SUCCESS,
	FETCH_END,
	FormWithRedirect,
	FunctionField,
	ReferenceField,
	ReferenceInput,
	SaveButton,
	TextInput,
	UPDATE,
	useMutation,
	useNotify,
} from 'react-admin';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';

import { OrderStatusCustomerEdit, UserRole } from '../../entities';

export const FieldButton = (props) => (
	<MUIButton color="primary" size="small" onClick={props.onClick} children={props.children} />
);


export const useEditableFieldStyles = makeStyles({
	root: {
		cursor: "pointer",
		display: "block",
		userSelect: "none",
		width: '100%',
		height: '100%',
		position: 'relative',
		zIndex: 1,
		
		'td > &:hover:before, td > span > &:hover:before': {
			content: '" "',
			zIndex: -1,
			position: 'absolute',
			width: 'calc(100% + 10px)',
			height: 'calc(100% + 14px)',
			left: -5,
			top: -7,
			// borderRadius: 2,
			// boxShadow: '0px 0px 4px rgba(63, 81, 181, 0.8)',
			// backgroundColor: 'rgba(63, 81, 181, 0.1)',
			backgroundColor: 'rgba(255, 255, 255, 0.4)',
			borderLeft: '1px solid rgba(0,0,0,0.1)',
			borderRight: '1px solid rgba(0,0,0,0.1)',
		}
	}
});


export const EditForm = ({ anchorEl, handleClose, handleSubmit, loading, edit, moreFields, btnLabel, ...props }) => {
	const filteredRecord = React.useMemo(() => {
		const arr = React.Children.toArray(edit, child => child.props.source);
		const result = arr.reduce((prev, curr) => {
			const val = props.record.hasOwnProperty(curr.props.source) ? props.record[curr.props.source] : (curr.props.defaultValue instanceof Function ? curr.props.defaultValue() : curr.props.defaultValue)
			const res = {
				...prev,
				[curr.props.source]: val
				// [curr.props.source]: props.record[curr.props.source] || curr.props.defaultValue,
			};
			return res;
		}, {});
		if (moreFields) {
			for (const field of moreFields) {
				result[field] = props.record[field];
			}
		}
		return result;
	} , [props.record, edit, moreFields]);


	return (
		<Popover
			open={true}
			onClose={handleClose}
			anchorEl={anchorEl}
			anchorOrigin={{
				vertical: 'bottom',
				horizontal: 'center',
			}}
			transformOrigin={{
				vertical: 'top',
				horizontal: 'center',
			}}
		>
			<FormWithRedirect
				style={{minWidth: 200}}
				save={handleSubmit} resource={props.resource}
				// record={props.record}
				record={filteredRecord}
				render={({ handleSubmitWithRedirect, saving, submitting }) => (
					<Box component="form" style={{width: 250}} p={2} display="flex" flexDirection="column" onSubmit={e => {
						e.preventDefault();
						e.stopPropagation();
						handleSubmitWithRedirect();
					}}>
						{edit}
						<SaveButton disabled={saving || submitting} saving={saving || submitting || loading} label={btnLabel} handleSubmitWithRedirect={handleSubmitWithRedirect} />
					</Box>
				)}
			/>
		</Popover>
	);
}

export const EditPopup = ({ render, edit, editable, updateType, prepareValues = v => v, additionalParams = [], ...props }) => {
	const classes = useEditableFieldStyles();
	const dispatch = useDispatch();
	const [anchorEl, setAnchorEl] = React.useState(null);

	const handleOpen = (e) => {
		e.stopPropagation();
		setAnchorEl(e.currentTarget);
		// e.preventDefault();
		// return false;
	};

	const handleClose = (e) => {
		setAnchorEl(null);
		// e.preventDefault();
		// e.stopPropagation();
		// return false;
	};
	const open = Boolean(anchorEl);
	const notify = useNotify();

	const [mutate, { loading }] = useMutation({
		type: updateType || 'updatePartial',
		resource: props.resource,
	}, {
		withDeclarativeSideEffectsSupport: true,
		onSuccess: ({ data }) => {
			setAnchorEl(null);
			dispatch({
				type: CRUD_UPDATE_SUCCESS,
				payload: { data },
				meta: {
					resource: props.resource,
					notification: {
						body: 'ra.notification.updated',
						level: 'info',
						messageArgs: {
							smart_count: 1,
						}
					},
					fetchResponse: UPDATE,
					fetchStatus: FETCH_END
				},
			})
			props.onSuccess && props.onSuccess();
		},
		onFailure: (error) => {
			notify(error.message, "error");
		}
	});

	const handleSubmit = async values => {
		const fields = React.Children.map(edit, child => child.props.source);
		const additionalData = additionalParams.reduce((additionalData, param) => {
			if (props.record[param] !== undefined) {
				additionalData[param] = props.record[param];
			}
			return additionalData;
		}, {});		
		const data = prepareValues ? prepareValues(values) : fields.reduce((prev, curr) => ({
			...prev, [curr]: values[curr]
		}), {});
		return mutate(
			{
				payload: {
					id: props.record.id,
					data: {
						...data,
						...additionalData,
					},
				}
			},
		);
	};

	const statusDisabled = OrderStatusCustomerEdit.indexOf(props.record.status) === -1;
	const isCustomerCompany = props.permissions && (
		props.permissions.role === UserRole.customer_manager || 
		props.permissions.role === UserRole.customer_employee
	);

	return (
		<React.Fragment>
			{((!isCustomerCompany || !statusDisabled) && open) && ReactDOM.createPortal((
				<EditForm anchorEl={anchorEl} handleSubmit={handleSubmit} handleClose={handleClose} loading={loading} edit={edit} {...props} />
			), document.body)}
			{editable === false ? render({}) : render({ openPopup: handleOpen, className: classes.root })}
		</React.Fragment>
	);
}


export const ReferenceEditableField = React.memo(({ children, editable, editField, allowEmpty, filter, optimized = false, ...props }) => {
	if (typeof editable === 'function') {
		editable = editable(props.record);
	}
	if (typeof editField === 'function') {
		editField = editField(props.record);
	}
	if (typeof filter === 'function') {
		filter = filter(props.record);
	}

	return (
		<EditPopup
			editable={editable}
			record={props.record}
			basePath={props.basePath}
			source={props.source}
			resource={props.resource}
			permissions={props.permissions}
			additionalParams={props.additionalParams}
			edit={(
				<ReferenceInput allowEmpty={allowEmpty ? allowEmpty : false} autoFocus label={props.label} source={props.source} reference={props.reference} filter={filter} {...props}>
					{editField}
				</ReferenceInput>
			)}
			render={({ openPopup, className }) => (
				<FunctionField
					render={r => {
						return (
							r[props.source] != null ?
								optimized ?
									<div {...props} className={className}>
										{React.cloneElement(children, {
											...children.props,
											className: className,
											onDoubleClick: openPopup,
											title: "Double click to edit",
										})}
									</div>
								:
									<ReferenceField {...props} title="Double click to edit" className={className} link={false}>
										{React.cloneElement(children, {
											...children.props,
											className: className,
											onDoubleClick: openPopup,
										})}
									</ReferenceField>
							:
								<div title="Double click to edit" className={className} onDoubleClick={openPopup}>&nbsp;</div>
						);
					}}
				/>
			)}
		/>
	);
}, (_old, _new) => _old.record.id === _new.record.id && _old.record[_old.source] === _new.record[_new.source])

ReferenceEditableField.defaultProps = {
    addLabel: true,
};


export const TextEditableField = React.memo(({ editable, title="Double click to edit", EditComponent=undefined, style={}, checkIsDisabled=undefined, editLabel=undefined, ...props }) => {
	const Component = EditComponent || TextInput;
	const isEditable = editable && (checkIsDisabled ? !checkIsDisabled(props.record) : true)

	return (
		<EditPopup
			editable={isEditable}
			source={props.source}
			resource={props.resource}
			record={props.record}
			basePath={props.basePath}
			edit={(
				<Component autoFocus
						   label={editLabel ? editLabel : props.label}
						   source={props.source} 
						   reference={props.reference} 
						   validate={props.validate} 
						   min={props.min} 
						   format={props.format}
						   parse={props.parse}
						   choices={props.choices}/>
			)}
			render={({ openPopup, className }) => (
				<FunctionField
					render={r => (
						<div 
							title={title} 
							className={className} 
							onDoubleClick={openPopup}
							style={style}
						>
							{r[props.source] != null ? (props.formatField ? props.formatField(r[props.source]) : r[props.source]) : '\u00A0'}
						</div>
					)}
				/>
			)}
		/>
	);
}, (prevProps, nextProps) => {
  return (
    prevProps.record.id === nextProps.record.id &&
    prevProps.record[prevProps.source] === nextProps.record[nextProps.source] &&
    prevProps.editable === nextProps.editable
  );
});

TextEditableField.defaultProps = {
    addLabel: true,
};


export const ButtonEditableField = React.memo(({ editable, children, title, color, variant, style, updateType, ...props }) => {
	return (
		<EditPopup
			editable={editable}
			source={props.source}
			resource={props.resource}
			record={props.record}
			basePath={props.basePath}
			edit={children}
			updateType={updateType}
			render={({ openPopup, className }) => (
				<FunctionField
					render={r => {
						return (
							<MUIButton onClick={openPopup} size="small" color={color || "primary"} variant={variant || "text"} style={style || {backgroundColor: "#fff", minWidth: 'auto'}}>
								{title}
							</MUIButton>
						);
					}}
				/>
			)}
		/>
	);
}, (_old, _new) => _old.record === _new.record);


export const ButtonEditableCustomField = React.memo(({ editable, children, title, color, style, variant, prepareValues, btnLabel, ...props }) => {

	return (
		<EditPopup
			prepareValues={prepareValues}
			editable={editable}
			source={props.source}
			resource={props.resource}
			record={props.record}
			basePath={props.basePath}
			moreFields={props.moreFields}
			edit={children}
			btnLabel={btnLabel}
			render={({ openPopup, className }) => (
				<FunctionField
					render={r => {
						return (
							<MUIButton onClick={openPopup} size="small" color={color || "primary"} variant={variant || "text"} style={style}>
								{title}
							</MUIButton>
						);
					}}
				/>
			)}
		/>
	);
}, (_old, _new) => _old.record === _new.record);

ButtonEditableField.defaultProps = {
    addLabel: true,
};
